import { RankedConstants } from '../../../../shared/GameEnums.ts';
import { BotDifficulty, BotsMatchType, MatchmakingMatchTypes, MODE_BASE_PLAYTIME_MINUTES, isBotMatchType } from '@colonist/game-base-types';
import { LobbyAction } from '@colonist/lobby-types';
import { ClientUserState } from '../ClientUserState.ts';
import { ViewPopupLogin } from '../ui_popup/ui_popup_membership_incentive/ViewPopupLogin.ts';
import { showBecomeMemberDialog } from '../ui_popup/showBecomeMemberDialog.ts';
import { AnalyticEventCategory, AnalyticEventType, AnalyticEventValue } from '../../../../shared/entity_datas/EntityDatas.ts';
import { UserSessionUtils } from '../UserSessionUtils.ts';
import { ErrorsMatchmaking } from '../../../../shared/errors/ErrorsMatchmaking.ts';
import { getNestedTranslation } from '@colonist/i18n';
import { showSimpleDialog } from '../ui_popup/showSimpleDialog.ts';
import { CK_FREE_GAME_COUNT, getCKRankedState, CKRankedState, shouldLockRankedCitiesAndKnights } from '../../../../shared/matchmaking/MatchmakingUtils.ts';
import { citiesAndKnightsMatchmakingMatchTypes, rankedMatchmakingMatchTypes } from '../../../../shared/matchmaking/MatchmakingEnums.ts';
import { ELOTypeEnum, LobbyImage } from '@colonist/types';
import { RankedUserStateController, RankedUserStateType } from '@colonist/leaderboard-types';
import { CA } from '../analytics/Analytics.ts';
import { UIImagePathController } from '../UIEnums.ts';
import { UIUtilsEjsVariables } from '../UIUtilsEjsVariables.ts';
import { getShuffleQueueModeName, getShuffleQueueMapName, getShuffleQueuePlayTimeForPlayerCount } from './ShuffleQueueUtils.ts';
import { SocketLobbySend } from '../socket/SocketLobbySend.ts';
import { StorageWrapper } from '../StorageWrapper.ts';
import { UIInternalVideoAdController } from '../ui_ad/UIInternalVideoAdController.ts';
import { ClientShuffleQueueController } from '../shuffle/ClientShuffleQueueController.ts';
import { TimeUtils } from '@colonist/utils';
import { createImgIn, setElementInnerText } from '../ui_utils/DomUtils.ts';
const rankedMatchTypeToEloTypeMap = {
    [MatchmakingMatchTypes.RankedClassic1v1]: ELOTypeEnum.VisibleRanked1v1,
    [MatchmakingMatchTypes.RankedClassic4P]: ELOTypeEnum.VisibleRanked4PClassic,
    [MatchmakingMatchTypes.RankedCitiesAndKnights4P]: ELOTypeEnum.VisibleRankedCitiesAndKnights4P,
    [MatchmakingMatchTypes.RankedRush4P]: ELOTypeEnum.VisibleRanked4PRush
};
const INTERNAL_VIDEO_AD_MATCH_TYPES = new Set([
    MatchmakingMatchTypes.RankedClassic1v1,
    MatchmakingMatchTypes.RankedClassic4P,
    MatchmakingMatchTypes.RankedRush4P
]);
const RUSH_BOT_AD_GAME_THRESHOLD = 10;
function shouldShowRushBotGameAd() {
    var _ClientUserState_userState;
    if ((_ClientUserState_userState = ClientUserState.userState) === null || _ClientUserState_userState === void 0 ? void 0 : _ClientUserState_userState.items.hideAds()) return false;
    return StorageWrapper.getRushBotGameCompletionCount() >= RUSH_BOT_AD_GAME_THRESHOLD;
}
export function getMatchmakingVideoAdType(selectedMatchType, findGameRankedStates, adblockIsActive) {
    var _ClientUserState_userState;
    if (!UIUtilsEjsVariables.rankedPrerollAdsEnabled) return undefined;
    if ((_ClientUserState_userState = ClientUserState.userState) === null || _ClientUserState_userState === void 0 ? void 0 : _ClientUserState_userState.items.hideAds()) return undefined;
    if (!hasFinishedPlacementGamesForSelectedMatchType(selectedMatchType, findGameRankedStates)) return undefined;
    const isWebDesktop = !UIUtilsEjsVariables.isUserOnMobileAppOrWeb && !UIUtilsEjsVariables.isUserOnDiscord;
    if (isWebDesktop) return getMatchmakingVideoAdTypeForWebDesktop(selectedMatchType, findGameRankedStates, adblockIsActive);
    if (INTERNAL_VIDEO_AD_MATCH_TYPES.has(selectedMatchType)) return 'internal';
    return undefined;
}
function getMatchmakingVideoAdTypeForWebDesktop(selectedMatchType, findGameRankedStates, adblockIsActive) {
    if (!adblockIsActive) return 'vli';
    if (adblockIsActive && INTERNAL_VIDEO_AD_MATCH_TYPES.has(selectedMatchType)) return 'internal';
    return undefined;
}
export function getMatchTypePlayTime(matchType) {
    switch(matchType){
        case BotsMatchType.BeginnerMode:
            return MODE_BASE_PLAYTIME_MINUTES.BOTS_BEGINNER_MODE;
        case BotsMatchType.Easy:
        case BotsMatchType.Medium:
        case BotsMatchType.Hard:
            return MODE_BASE_PLAYTIME_MINUTES.BOTS_STANDARD;
        case BotsMatchType.ColonistRush:
            return MODE_BASE_PLAYTIME_MINUTES.BOTS_COLONIST_RUSH;
        case MatchmakingMatchTypes.UnrankedClassic1v1:
        case MatchmakingMatchTypes.RankedClassic1v1:
            return MODE_BASE_PLAYTIME_MINUTES.CLASSIC_1V1;
        case MatchmakingMatchTypes.UnrankedClassic4P:
            return 30;
        case MatchmakingMatchTypes.RankedClassic4P:
            return MODE_BASE_PLAYTIME_MINUTES.CLASSIC;
        case MatchmakingMatchTypes.RankedCitiesAndKnights4P:
            return MODE_BASE_PLAYTIME_MINUTES.CITIES_AND_KNIGHTS;
        case MatchmakingMatchTypes.RankedRush4P:
            return MODE_BASE_PLAYTIME_MINUTES.COLONIST_RUSH;
        case MatchmakingMatchTypes.UnrankedShuffle:
            var _ClientShuffleQueueController_shuffleQueueData, _ClientShuffleQueueController_shuffleQueueData1;
            return getShuffleQueuePlayTimeForPlayerCount((_ClientShuffleQueueController_shuffleQueueData = ClientShuffleQueueController.shuffleQueueData) === null || _ClientShuffleQueueController_shuffleQueueData === void 0 ? void 0 : _ClientShuffleQueueController_shuffleQueueData.playerCount, (_ClientShuffleQueueController_shuffleQueueData1 = ClientShuffleQueueController.shuffleQueueData) === null || _ClientShuffleQueueController_shuffleQueueData1 === void 0 ? void 0 : _ClientShuffleQueueController_shuffleQueueData1.modeId);
        default:
            return undefined;
    }
}
export function getMatchTypePlayerAndBotCount(matchType) {
    switch(matchType){
        case BotsMatchType.BeginnerMode:
            return {
                playerCount: 1,
                botCount: 0
            };
        case BotsMatchType.Easy:
        case BotsMatchType.Medium:
        case BotsMatchType.Hard:
        case BotsMatchType.ColonistRush:
            return {
                playerCount: 1,
                botCount: 3
            };
        case MatchmakingMatchTypes.UnrankedClassic1v1:
        case MatchmakingMatchTypes.RankedClassic1v1:
            return {
                playerCount: 2,
                botCount: 0
            };
        case MatchmakingMatchTypes.UnrankedClassic4P:
        case MatchmakingMatchTypes.RankedClassic4P:
        case MatchmakingMatchTypes.RankedCitiesAndKnights4P:
        case MatchmakingMatchTypes.RankedRush4P:
            return {
                playerCount: 4,
                botCount: 0
            };
        case MatchmakingMatchTypes.UnrankedShuffle:
            {
                const shuffleQueueData = ClientShuffleQueueController.shuffleQueueData;
                if (shuffleQueueData == undefined) return {
                    playerCount: 0,
                    botCount: 0
                };
                const totalPlayerCount = shuffleQueueData.playerCount;
                if (shuffleQueueData.isBotGame == true) return {
                    playerCount: 1,
                    botCount: totalPlayerCount - 1
                };
                return {
                    playerCount: totalPlayerCount,
                    botCount: 0
                };
            }
        default:
            return {
                playerCount: 0,
                botCount: 0
            };
    }
}
export function getMatchTypeEligibilityAndCreatePopup(selectedMatchType) {
    var _ClientUserState_userState, _ClientUserState_userState1, _ClientUserState_userState2, _ClientUserState_userState3, _ClientUserState_userState4;
    const eligibleMatchTypeResult = {
        isEligible: true,
        createPopup: undefined
    };
    if (isBotMatchType(selectedMatchType)) return eligibleMatchTypeResult;
    const isRankedMatchTypeSelected = isRankedMatchType(selectedMatchType);
    const isShuffleMatchTypeSelected = selectedMatchType == MatchmakingMatchTypes.UnrankedShuffle;
    if (!isRankedMatchTypeSelected && !isShuffleMatchTypeSelected) return eligibleMatchTypeResult;
    if (!((_ClientUserState_userState = ClientUserState.userState) === null || _ClientUserState_userState === void 0 ? void 0 : _ClientUserState_userState.isLoggedIn)) return {
        isEligible: false,
        createPopup: ()=>new ViewPopupLogin()
    };
    if (isShuffleMatchTypeSelected) {
        const isEligibleForShuffle = UserSessionUtils.userEligibleForRanked();
        if (!isEligibleForShuffle) {
            const shuffleLockedError = new ErrorsMatchmaking.ShuffleLocked(ClientUserState.userState.totalCompletedGameCount, RankedConstants.NumberOfGamesToUnlockRanked);
            return {
                isEligible: false,
                createPopup: ()=>showMatchmakingErrorPopup(shuffleLockedError)
            };
        }
        const playerData = ClientShuffleQueueController.shufflePlayerData;
        const hasMembership = ClientUserState.userState.items.hasMembership();
        const hasFreeGamesRemaining = playerData != undefined && playerData.totalGameCount < playerData.freeGamesTotalCount;
        const hasDailyFreeGame = (playerData === null || playerData === void 0 ? void 0 : playerData.playedToday) === false;
        if (hasMembership) return eligibleMatchTypeResult;
        if (hasFreeGamesRemaining) return eligibleMatchTypeResult;
        if (hasDailyFreeGame) return eligibleMatchTypeResult;
        return {
            isEligible: false,
            createPopup: ()=>{
                showBecomeMemberDialog('gameplay', AnalyticEventValue.PopupMembershipIncentivesShuffleQueue);
            }
        };
    }
    const isCitiesAndKnightsMatchTypeSelected = isCitiesAndKnightsMatchType(selectedMatchType);
    const shouldLockCitiesAndKnights = shouldLockRankedCitiesAndKnights((_ClientUserState_userState1 = ClientUserState.userState) === null || _ClientUserState_userState1 === void 0 ? void 0 : _ClientUserState_userState1.items, (_ClientUserState_userState2 = ClientUserState.userState) === null || _ClientUserState_userState2 === void 0 ? void 0 : _ClientUserState_userState2.ckTotalGameCount, ClientUserState.abTests, (_ClientUserState_userState3 = ClientUserState.userState) === null || _ClientUserState_userState3 === void 0 ? void 0 : _ClientUserState_userState3.ckNextRerollAt, (_ClientUserState_userState4 = ClientUserState.userState) === null || _ClientUserState_userState4 === void 0 ? void 0 : _ClientUserState_userState4.ckFreeGameUntil);
    if (isCitiesAndKnightsMatchTypeSelected && shouldLockCitiesAndKnights) {
        return {
            isEligible: false,
            createPopup: ()=>{
                showBecomeMemberDialog('gameplay', AnalyticEventValue.PopupMembershipIncentivesRankedCitiesAndKnights);
            }
        };
    }
    const isEligibleForRanked = UserSessionUtils.userEligibleForRanked();
    if (isEligibleForRanked) return eligibleMatchTypeResult;
    const requiredNumberOfGamesForRanked = RankedConstants.NumberOfGamesToUnlockRanked;
    const rankedLockedError = new ErrorsMatchmaking.RankedLocked(ClientUserState.userState.totalCompletedGameCount, requiredNumberOfGamesForRanked);
    return {
        isEligible: false,
        createPopup: ()=>showMatchmakingErrorPopup(rankedLockedError)
    };
}
export function getMatchTypeDetails(selectedMatchType, findGameRankedStates) {
    var _ClientUserState_userState;
    if (selectedMatchType == MatchmakingMatchTypes.UnrankedShuffle) return getShuffleQueueDetails();
    const isRankedMatchTypeSelected = isRankedMatchType(selectedMatchType);
    if (!isRankedMatchTypeSelected) {
        return {
            description: undefined,
            badge: undefined
        };
    }
    if (!((_ClientUserState_userState = ClientUserState.userState) === null || _ClientUserState_userState === void 0 ? void 0 : _ClientUserState_userState.isLoggedIn)) {
        return {
            description: {
                key: 'strings:homePage.ranked.logInToPlay'
            },
            showDescriptionLockIcon: true,
            badge: undefined
        };
    }
    const isCitiesAndKnightsMatchTypeSelected = isCitiesAndKnightsMatchType(selectedMatchType);
    let ckBadge;
    if (isCitiesAndKnightsMatchTypeSelected) {
        const ckRankedCardData = getCKRankedCardData();
        if ((ckRankedCardData === null || ckRankedCardData === void 0 ? void 0 : ckRankedCardData.description) != undefined) return ckRankedCardData;
        ckBadge = ckRankedCardData === null || ckRankedCardData === void 0 ? void 0 : ckRankedCardData.badge;
    }
    const isEligibleForRanked = UserSessionUtils.userEligibleForRanked();
    if (!isEligibleForRanked) {
        const requiredNumberOfGamesForRanked = RankedConstants.NumberOfGamesToUnlockRanked;
        const completedGames = ClientUserState.userState.totalCompletedGameCount;
        return {
            description: undefined,
            badge: undefined,
            progressBar: {
                current: completedGames,
                total: requiredNumberOfGamesForRanked,
                label: {
                    key: 'strings:homePage.matchmakingSelect.casualGamesToUnlock',
                    options: {
                        current: completedGames,
                        total: requiredNumberOfGamesForRanked
                    }
                },
                showLockIcon: true
            }
        };
    }
    const expectedEloType = rankedMatchTypeToEloTypeMap[selectedMatchType];
    const findGameRankedState = findGameRankedStates === null || findGameRankedStates === void 0 ? void 0 : findGameRankedStates.find((gameState)=>gameState.eloType === expectedEloType);
    if ((findGameRankedState === null || findGameRankedState === void 0 ? void 0 : findGameRankedState.rankedState) != undefined) {
        const rankedState = RankedUserStateController.deserialize(findGameRankedState.rankedState);
        if (rankedState.type == RankedUserStateType.Placement) {
            const placementState = rankedState;
            var _placementState_numberOfPlacementGamesFinished;
            const placementGamesFinished = (_placementState_numberOfPlacementGamesFinished = placementState.numberOfPlacementGamesFinished) !== null && _placementState_numberOfPlacementGamesFinished !== void 0 ? _placementState_numberOfPlacementGamesFinished : 0;
            var _placementState_numberOfPlacementGamesRequired;
            const placementGamesRequired = (_placementState_numberOfPlacementGamesRequired = placementState.numberOfPlacementGamesRequired) !== null && _placementState_numberOfPlacementGamesRequired !== void 0 ? _placementState_numberOfPlacementGamesRequired : 10;
            return {
                description: undefined,
                badge: ckBadge,
                progressBar: {
                    current: placementGamesFinished,
                    total: placementGamesRequired,
                    label: {
                        key: 'strings:homePage.matchmakingSelect.placementsToRank',
                        options: {
                            current: placementGamesFinished,
                            total: placementGamesRequired
                        }
                    }
                }
            };
        }
        const rankedMMRState = rankedState;
        var _rankedMMRState_currentMMR;
        const currentMMR = (_rankedMMRState_currentMMR = rankedMMRState.currentMMR) !== null && _rankedMMRState_currentMMR !== void 0 ? _rankedMMRState_currentMMR : 0;
        const divisionType = rankedMMRState.currentDivisionState.divisionType;
        const divisionIcon = UIImagePathController.getRankedDivisionImage(divisionType);
        return {
            description: undefined,
            badge: {
                text: {
                    key: 'strings:colonistCommon.utils.#buffer',
                    options: {
                        value: Math.round(currentMMR)
                    }
                },
                icon: divisionIcon
            }
        };
    }
    return {
        description: undefined,
        badge: ckBadge
    };
}
function getShuffleQueueDetails() {
    if (ClientUserState.userState == undefined || !ClientUserState.userState.isLoggedIn) {
        const guestQueueData = ClientShuffleQueueController.shuffleQueueData;
        return {
            subtitle: guestQueueData != undefined ? getShuffleQueueMapAndModeDescription(guestQueueData) : {
                key: 'strings:homePage.ranked.logInToSeeShuffleQueue'
            },
            description: {
                key: 'strings:homePage.ranked.logInToPlay'
            },
            showDescriptionLockIcon: true,
            badge: undefined
        };
    }
    const queueData = ClientShuffleQueueController.shuffleQueueData;
    const requiredGamesForShuffle = RankedConstants.NumberOfGamesToUnlockRanked;
    const completedGames = ClientUserState.userState.totalCompletedGameCount;
    if (completedGames < requiredGamesForShuffle) {
        return {
            description: queueData != undefined ? getShuffleQueueMapAndModeDescription(queueData) : undefined,
            badge: undefined,
            progressBar: {
                current: completedGames,
                total: requiredGamesForShuffle,
                label: {
                    key: 'strings:homePage.matchmakingSelect.casualGamesToUnlock',
                    options: {
                        current: completedGames,
                        total: requiredGamesForShuffle
                    }
                },
                showLockIcon: true
            }
        };
    }
    const playerData = ClientShuffleQueueController.shufflePlayerData;
    if (queueData == undefined) {
        return {
            description: undefined,
            badge: undefined
        };
    }
    if (ClientUserState.userState.items.hasMembership() || playerData == undefined) {
        return {
            description: getShuffleQueueMapAndModeDescription(queueData),
            badge: undefined
        };
    }
    const shuffleGamesPlayed = playerData.totalGameCount;
    const freeGamesTotalCount = playerData.freeGamesTotalCount;
    if (shuffleGamesPlayed < freeGamesTotalCount) {
        const shuffleGamesLeft = freeGamesTotalCount - shuffleGamesPlayed;
        return {
            description: getShuffleQueueMapAndModeDescription(queueData),
            badge: {
                text: {
                    key: 'strings:homePage.matchmakingSelect.freeGamesLeft',
                    options: {
                        count: shuffleGamesLeft
                    }
                },
                icon: LobbyImage.Gift,
                backgroundColorClass: 'mm-badge-green',
                placement: 'bottomRow'
            }
        };
    }
    if (!playerData.playedToday) {
        return {
            description: getShuffleQueueMapAndModeDescription(queueData),
            badge: {
                text: {
                    key: 'strings:homePage.matchmakingSelect.dailyFreeShuffleGame'
                },
                icon: LobbyImage.Gift,
                backgroundColorClass: 'mm-badge-green',
                placement: 'bottomRow'
            }
        };
    }
    const endOfDay = TimeUtils.adjustDateByDays(1, TimeUtils.getStartOfDay(new Date()));
    const timeUntilFreePlay = TimeUtils.formatCountdownString(endOfDay);
    return {
        description: getShuffleQueueMapAndModeDescription(queueData),
        badge: {
            text: {
                key: 'strings:homePage.matchmakingSelect.dailyFreeShuffleGameWaiting',
                options: {
                    time: timeUntilFreePlay
                }
            },
            icon: LobbyImage.IconTimer,
            backgroundColorClass: 'mm-badge-yellow',
            placement: 'bottomRow'
        }
    };
}
export function getShuffleQueueMapAndModeDescription(queueData) {
    const mapName = getShuffleQueueMapName(queueData.mapId);
    const modeName = getShuffleQueueModeName(queueData.modeId);
    const isBotGame = queueData.isBotGame;
    if (!mapName) return {
        key: 'strings:homePage.shuffleQueue.mapAndModeUnknown'
    };
    if (!modeName) return getNestedTranslation({
        key: 'strings:homePage.shuffleQueue.mapOnly',
        options: {
            context: isBotGame == true ? 'withBots' : ''
        }
    }, {
        map: mapName
    });
    return getNestedTranslation({
        key: 'strings:homePage.shuffleQueue.mapAndMode',
        options: {
            context: isBotGame == true ? 'withBots' : ''
        }
    }, {
        map: mapName,
        mode: modeName
    });
}
export function showMatchmakingErrorPopup(error) {
    showSimpleDialog({
        title: error.title,
        body: error.description
    });
}
export function getClientCKRankedState() {
    var _ClientUserState_userState, _ClientUserState_userState1, _ClientUserState_userState2, _ClientUserState_userState3, _ClientUserState_userState4;
    const state = getCKRankedState((_ClientUserState_userState = ClientUserState.userState) === null || _ClientUserState_userState === void 0 ? void 0 : _ClientUserState_userState.items, (_ClientUserState_userState1 = ClientUserState.userState) === null || _ClientUserState_userState1 === void 0 ? void 0 : _ClientUserState_userState1.ckTotalGameCount, ClientUserState.abTests, (_ClientUserState_userState2 = ClientUserState.userState) === null || _ClientUserState_userState2 === void 0 ? void 0 : _ClientUserState_userState2.ckNextRerollAt, (_ClientUserState_userState3 = ClientUserState.userState) === null || _ClientUserState_userState3 === void 0 ? void 0 : _ClientUserState_userState3.ckFreeGameUntil);
    const canRollDice = state == CKRankedState.CanRollDice;
    var _ClientUserState_userState_ckEligibleRolls;
    const eligibleRolls = canRollDice ? (_ClientUserState_userState_ckEligibleRolls = (_ClientUserState_userState4 = ClientUserState.userState) === null || _ClientUserState_userState4 === void 0 ? void 0 : _ClientUserState_userState4.ckEligibleRolls) !== null && _ClientUserState_userState_ckEligibleRolls !== void 0 ? _ClientUserState_userState_ckEligibleRolls : [] : [];
    return {
        state: state,
        eligibleRolls: eligibleRolls
    };
}
function getCKRankedCardData() {
    const { state: ckRankedState } = getClientCKRankedState();
    switch(ckRankedState){
        case CKRankedState.Locked:
            return {
                description: {
                    key: 'strings:homePage.becomeAMemberToUnlock'
                },
                badge: undefined
            };
        case CKRankedState.HasFreeGames:
            var _ClientUserState_userState;
            var _ClientUserState_userState_ckTotalGameCount;
            return {
                description: undefined,
                badge: {
                    text: {
                        key: 'strings:homePage.matchmakingSelect.freeGamesLeft',
                        options: {
                            count: CK_FREE_GAME_COUNT - ((_ClientUserState_userState_ckTotalGameCount = (_ClientUserState_userState = ClientUserState.userState) === null || _ClientUserState_userState === void 0 ? void 0 : _ClientUserState_userState.ckTotalGameCount) !== null && _ClientUserState_userState_ckTotalGameCount !== void 0 ? _ClientUserState_userState_ckTotalGameCount : 0)
                        }
                    },
                    icon: LobbyImage.Gift,
                    backgroundColorClass: 'mm-badge-green',
                    placement: 'bottomRow'
                }
            };
        case CKRankedState.HasFreeGameFromDiceRoll:
            var _ClientUserState_userState1;
            const timeUntilFreePlay = TimeUtils.formatCountdownString((_ClientUserState_userState1 = ClientUserState.userState) === null || _ClientUserState_userState1 === void 0 ? void 0 : _ClientUserState_userState1.ckFreeGameUntil);
            return {
                description: undefined,
                badge: {
                    text: {
                        key: 'strings:homePage.matchmakingSelect.freeGameFromDiceRoll',
                        options: {
                            time: timeUntilFreePlay
                        }
                    },
                    icon: LobbyImage.Gift,
                    backgroundColorClass: 'mm-badge-orange',
                    placement: 'bottomRow'
                }
            };
        case CKRankedState.CanRollDice:
            return {
                description: undefined,
                badge: {
                    text: {
                        key: 'strings:homePage.matchmakingSelect.dailyFreeRoll'
                    },
                    icon: LobbyImage.Dice3Sharp,
                    backgroundColorClass: 'mm-badge-green',
                    placement: 'bottomRow'
                }
            };
        case CKRankedState.WaitingToRollDice:
            var _ClientUserState_userState2;
            const timeUntilDiceRoll = TimeUtils.formatCountdownString((_ClientUserState_userState2 = ClientUserState.userState) === null || _ClientUserState_userState2 === void 0 ? void 0 : _ClientUserState_userState2.ckNextRerollAt);
            return {
                description: undefined,
                badge: {
                    text: {
                        key: 'strings:homePage.matchmakingSelect.dailyFreeRollWaiting',
                        options: {
                            time: timeUntilDiceRoll
                        }
                    },
                    icon: LobbyImage.IconTimer,
                    backgroundColorClass: 'mm-badge-yellow',
                    placement: 'bottomRow'
                }
            };
        default:
            return undefined;
    }
}
function isCitiesAndKnightsMatchType(selectedMatchType) {
    return citiesAndKnightsMatchmakingMatchTypes.has(selectedMatchType);
}
export function isRankedMatchType(selectedMatchType) {
    return rankedMatchmakingMatchTypes.has(selectedMatchType);
}
export function hasFinishedPlacementGamesForSelectedMatchType(selectedMatchType, findGameRankedStates) {
    const expectedEloType = rankedMatchTypeToEloTypeMap[selectedMatchType];
    if (expectedEloType == undefined) return false;
    const findGameRankedState = findGameRankedStates.find((gameState)=>gameState.eloType == expectedEloType);
    if ((findGameRankedState === null || findGameRankedState === void 0 ? void 0 : findGameRankedState.rankedState) == undefined) return false;
    const rankedState = RankedUserStateController.deserialize(findGameRankedState.rankedState);
    if (rankedState.type == RankedUserStateType.Placement) {
        const placementState = rankedState;
        return placementState.finishedPlacementGames();
    }
    return true;
}
const matchTypeToStringMap = {
    [MatchmakingMatchTypes.UnrankedClassic4P]: 'UnrankedClassic4P',
    [MatchmakingMatchTypes.RankedClassic4P]: 'RankedClassic4P',
    [MatchmakingMatchTypes.UnrankedClassic1v1]: 'UnrankedClassic1v1',
    [MatchmakingMatchTypes.RankedClassic1v1]: 'RankedClassic1v1',
    [MatchmakingMatchTypes.RankedCitiesAndKnights4P]: 'RankedCitiesAndKnights4P',
    [MatchmakingMatchTypes.UnrankedShuffle]: 'UnrankedShuffle',
    [MatchmakingMatchTypes.RankedRush4P]: 'RankedRush4P'
};
export function getGameModeStringForAnalytics(matchType) {
    if (isBotMatchType(matchType)) {
        const difficulty = matchType.charAt(0).toUpperCase() + matchType.slice(1).toLowerCase();
        return `PlayVsBots_${difficulty}`;
    }
    var _matchTypeToStringMap_matchType;
    return (_matchTypeToStringMap_matchType = matchTypeToStringMap[matchType]) !== null && _matchTypeToStringMap_matchType !== void 0 ? _matchTypeToStringMap_matchType : 'Unknown';
}
export function trackMatchmakingTimeToQueueStart(componentInitTimestamp, gameModeString) {
    if (!UIUtilsEjsVariables.isUserOnMobileAppOrWeb) return;
    const timeToClickInSeconds = (Date.now() - componentInitTimestamp) / 1000;
    void CA.sendAnalyticEvent({
        type: AnalyticEventType.MatchmakingUITimeToFirstQueueStart,
        category: AnalyticEventCategory.Matchmaking,
        floatValue: timeToClickInSeconds,
        stringValue: gameModeString
    });
}
export function startBotGame(botMatchType, mapSetting) {
    switch(botMatchType){
        case BotsMatchType.BeginnerMode:
            void SocketLobbySend.continueCampaignMode();
            break;
        case BotsMatchType.Easy:
            void SocketLobbySend.clickedStartBotGame(BotDifficulty.BotEasy, LobbyAction.ClickedBotGame, mapSetting);
            break;
        case BotsMatchType.Medium:
            void SocketLobbySend.clickedStartBotGame(BotDifficulty.BotMedium, LobbyAction.ClickedBotGame, mapSetting);
            break;
        case BotsMatchType.Hard:
            void SocketLobbySend.clickedStartBotGame(BotDifficulty.BotHard, LobbyAction.ClickedBotGame, mapSetting);
            break;
        case BotsMatchType.ColonistRush:
            {
                const rushDifficulty = StorageWrapper.getRushBotDifficulty();
                if (shouldShowRushBotGameAd()) {
                    void UIInternalVideoAdController.start(BotsMatchType.ColonistRush, ()=>void SocketLobbySend.clickedStartBotGame(rushDifficulty, LobbyAction.ClickedRushBotGame, mapSetting));
                } else {
                    void SocketLobbySend.clickedStartBotGame(rushDifficulty, LobbyAction.ClickedRushBotGame, mapSetting);
                }
                break;
            }
    }
}
function isMatchTypeLoginLocked(matchType) {
    var _ClientUserState_userState;
    if ((_ClientUserState_userState = ClientUserState.userState) === null || _ClientUserState_userState === void 0 ? void 0 : _ClientUserState_userState.isLoggedIn) return false;
    if (isBotMatchType(matchType)) return false;
    if (isRankedMatchType(matchType)) return true;
    if (matchType == MatchmakingMatchTypes.UnrankedShuffle) return true;
    return false;
}
function isMatchTypeMembershipLocked(matchType) {
    if (matchType == MatchmakingMatchTypes.RankedCitiesAndKnights4P) {
        const { state: ckState } = getClientCKRankedState();
        if (ckState == CKRankedState.Locked) return true;
        if (ckState == CKRankedState.WaitingToRollDice) return true;
        return false;
    }
    if (matchType == MatchmakingMatchTypes.UnrankedShuffle) {
        if (ClientShuffleQueueController.shufflePlayerData == undefined) return false;
        const { isEligible } = getMatchTypeEligibilityAndCreatePopup(MatchmakingMatchTypes.UnrankedShuffle);
        return !isEligible;
    }
    return false;
}
export function isMatchTypeCasualLocked(matchType) {
    var _ClientUserState_userState;
    if (isBotMatchType(matchType)) return false;
    const mmMatchType = matchType;
    const isRanked = isRankedMatchType(mmMatchType);
    const isShuffle = mmMatchType == MatchmakingMatchTypes.UnrankedShuffle;
    const isNotRankedOrShuffle = !isRanked && !isShuffle;
    if (isNotRankedOrShuffle) return false;
    if (!((_ClientUserState_userState = ClientUserState.userState) === null || _ClientUserState_userState === void 0 ? void 0 : _ClientUserState_userState.isLoggedIn)) return false;
    if (isShuffle) {
        var _ClientUserState_userState1;
        const requiredGames = RankedConstants.NumberOfGamesToUnlockRanked;
        var _ClientUserState_userState_totalCompletedGameCount;
        return ((_ClientUserState_userState_totalCompletedGameCount = (_ClientUserState_userState1 = ClientUserState.userState) === null || _ClientUserState_userState1 === void 0 ? void 0 : _ClientUserState_userState1.totalCompletedGameCount) !== null && _ClientUserState_userState_totalCompletedGameCount !== void 0 ? _ClientUserState_userState_totalCompletedGameCount : 0) < requiredGames;
    }
    const isCK = isCitiesAndKnightsMatchType(mmMatchType);
    if (isCK) {
        var _ClientUserState_userState2, _ClientUserState_userState3, _ClientUserState_userState4, _ClientUserState_userState5;
        const shouldLockCK = shouldLockRankedCitiesAndKnights((_ClientUserState_userState2 = ClientUserState.userState) === null || _ClientUserState_userState2 === void 0 ? void 0 : _ClientUserState_userState2.items, (_ClientUserState_userState3 = ClientUserState.userState) === null || _ClientUserState_userState3 === void 0 ? void 0 : _ClientUserState_userState3.ckTotalGameCount, ClientUserState.abTests, (_ClientUserState_userState4 = ClientUserState.userState) === null || _ClientUserState_userState4 === void 0 ? void 0 : _ClientUserState_userState4.ckNextRerollAt, (_ClientUserState_userState5 = ClientUserState.userState) === null || _ClientUserState_userState5 === void 0 ? void 0 : _ClientUserState_userState5.ckFreeGameUntil);
        if (shouldLockCK) return false;
    }
    return !UserSessionUtils.userEligibleForRanked();
}
export function getCasualRedirectMatchType(matchType) {
    switch(matchType){
        case MatchmakingMatchTypes.UnrankedShuffle:
        case MatchmakingMatchTypes.RankedClassic1v1:
            return MatchmakingMatchTypes.UnrankedClassic1v1;
        case MatchmakingMatchTypes.RankedClassic4P:
        case MatchmakingMatchTypes.RankedCitiesAndKnights4P:
        case MatchmakingMatchTypes.RankedRush4P:
            return MatchmakingMatchTypes.UnrankedClassic4P;
        default:
            return undefined;
    }
}
export function updateMatchmakingPlayButtonState(button, matchType, options) {
    const isLoginLocked = isMatchTypeLoginLocked(matchType);
    const isCasualLocked = isMatchTypeCasualLocked(matchType);
    const shouldShowMembershipButton = options.shouldShowMembershipButton == true || isMatchTypeMembershipLocked(matchType);
    const isMembershipLocked = !isCasualLocked && shouldShowMembershipButton;
    const isPremiumMembershipButton = isMembershipLocked && !isLoginLocked && options.membershipButtonVariant == 'premium';
    const isPlusMembershipButton = isMembershipLocked && !isLoginLocked && options.membershipButtonVariant == 'plus';
    button.classList.toggle('mm-button-login', isLoginLocked);
    button.classList.toggle('mm-button-membership-plus', isPlusMembershipButton);
    button.classList.toggle('mm-button-membership-premium', isPremiumMembershipButton);
    if (isLoginLocked) {
        setElementInnerText(button, {
            key: 'strings:homePage.header.login'
        });
        return;
    }
    if (isCasualLocked) {
        setElementInnerText(button, {
            key: 'strings:homePage.matchmakingSelect.playCasualToUnlock'
        });
        return;
    }
    if (isMembershipLocked) {
        setElementInnerText(button, {
            key: 'strings:homePage.becomeAMember'
        });
        const membershipIcon = isPremiumMembershipButton ? LobbyImage.StoreMembershipPremium : LobbyImage.StoreMembershipPlus;
        const icon = createImgIn(button, membershipIcon, 'mm-button-member-icon');
        button.prepend(icon);
        return;
    }
    setElementInnerText(button, {
        key: 'strings:homePage.matchmakingSelect.playButton'
    });
}
